/**
 * Settings Page Component
 *
 * Main settings page that assembles priority settings, Google settings, and other
 * configuration options into a cohesive layout using Polaris Page and Layout components.
 *
 * Sections:
 * - Content Priorities: Priority settings for different content types
 * - Google Integration: Google-specific settings (conditionally shown when connected)
 *
 * @see docs/PRD.md for settings requirements
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Banner,
  SkeletonBodyText,
  BlockStack,
} from '@shopify/polaris';

import { PrioritySettingsForm } from '../components/PrioritySettingsForm';
import { GoogleSettingsForm } from '../components/GoogleSettingsForm';

import {
  fetchSettings,
  fetchGoogleStatus,
} from '../lib/api-client';
import type {
  MerchantSettings,
  GoogleConnectionStatus,
} from '../types/api';

// =============================================================================
// Types
// =============================================================================

interface SettingsState {
  /** Merchant settings data */
  settings: MerchantSettings | null;
  /** Google connection status */
  googleStatus: GoogleConnectionStatus | null;
  /** Whether initial data is loading */
  loading: boolean;
  /** Error message for settings fetch */
  settingsError: string | null;
  /** Error message for Google status fetch */
  googleError: string | null;
}

// =============================================================================
// Settings Component
// =============================================================================

/**
 * Main Settings page component.
 *
 * Features:
 * - Fetches settings and Google status on mount via api-client
 * - Shows loading skeletons while data loads
 * - Shows error banners if API calls fail
 * - Content Priorities section with PrioritySettingsForm
 * - Google Integration section with GoogleSettingsForm (conditional)
 * - Annotated sections with descriptions
 */
export function Settings() {
  const [state, setState] = useState<SettingsState>({
    settings: null,
    googleStatus: null,
    loading: true,
    settingsError: null,
    googleError: null,
  });

  /**
   * Fetch all settings data
   */
  const fetchAllData = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      loading: true,
      settingsError: null,
      googleError: null,
    }));

    // Fetch all data in parallel
    const [settingsResult, googleResult] = await Promise.all([
      fetchSettings(),
      fetchGoogleStatus(),
    ]);

    setState((prev) => ({
      ...prev,
      loading: false,
      settings: settingsResult.success ? settingsResult.data : prev.settings,
      googleStatus: googleResult.success ? googleResult.data : prev.googleStatus,
      settingsError: settingsResult.success ? null : settingsResult.error.error,
      googleError: googleResult.success ? null : googleResult.error.error,
    }));
  }, []);

  /**
   * Handle settings change (refresh data)
   */
  const handleSettingsChange = useCallback(() => {
    fetchAllData();
  }, [fetchAllData]);

  /**
   * Handle Google disconnect
   */
  const handleGoogleDisconnect = useCallback(() => {
    // Update local state to reflect disconnection
    setState((prev) => ({
      ...prev,
      googleStatus: prev.googleStatus
        ? { ...prev.googleStatus, connected: false }
        : null,
    }));
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const {
    settings,
    googleStatus,
    loading,
    settingsError,
    googleError,
  } = state;

  // Check if all fetches failed
  const hasGlobalError = settingsError && googleError;

  return (
    <Page
      title="Settings"
      backAction={{ content: 'Dashboard', url: '/' }}
    >
      <Layout>
        {/* Global error banner when all fetches fail */}
        {hasGlobalError && (
          <Layout.Section>
            <Banner
              title="Unable to load settings"
              tone="critical"
              action={{
                content: 'Retry',
                onAction: fetchAllData,
              }}
            >
              Please check your connection and try again. If the problem
              persists, contact support.
            </Banner>
          </Layout.Section>
        )}

        {/* Content Priorities Section */}
        <Layout.AnnotatedSection
          id="content-priorities"
          title="Content Priorities"
          description="Control how different content types are prioritized when notifying search engines. High priority items use Google quota first, while ignored items only use IndexNow."
        >
          {loading ? (
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Content Priority Settings
                </Text>
                <SkeletonBodyText lines={8} />
              </BlockStack>
            </Card>
          ) : (
            <PrioritySettingsForm
              onSaveSuccess={handleSettingsChange}
            />
          )}
        </Layout.AnnotatedSection>

        {/* Google Integration Section - Only shown when Google is connected */}
        {(loading || googleStatus?.connected) && (
          <Layout.AnnotatedSection
            id="google-integration"
            title="Google Integration"
            description="Configure Google Indexing API settings including quota limits and stale URL handling. These settings control how Pingdex interacts with Google's API."
          >
            {loading ? (
              <Card>
                <BlockStack gap="400">
                  <Text as="h3" variant="headingSm">
                    Google Settings
                  </Text>
                  <SkeletonBodyText lines={6} />
                </BlockStack>
              </Card>
            ) : (
              <GoogleSettingsForm
                googleStatus={googleStatus}
                settings={settings}
                loading={false}
                error={!hasGlobalError ? googleError : null}
                onSettingsChange={handleSettingsChange}
                onGoogleDisconnect={handleGoogleDisconnect}
              />
            )}
          </Layout.AnnotatedSection>
        )}
      </Layout>
    </Page>
  );
}
